import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEtsyProduct } from '@/lib/etsy/products'

export default function EtsyProductDetail() {
  const { brandId, shopId, listingId } = useParams<{
    brandId: string
    shopId: string
    listingId: string
  }>()

  const listHref =
    brandId && shopId ? `/brands/${brandId}/integrations/etsy/products?shopId=${encodeURIComponent(shopId)}` : '/brands/picker'

  const { data, isLoading, error } = useQuery({
    queryKey: ['etsy', 'product', brandId, shopId, listingId],
    queryFn: () => getEtsyProduct(brandId!, shopId!, listingId!),
    enabled: !!brandId && !!shopId && !!listingId,
  })

  const listing = data?.listing
  const media = useMemo(() => {
    const raw = (listing as { images?: Array<Record<string, unknown>> } | undefined)?.images
    if (!Array.isArray(raw)) return []
    return raw
      .map((img) => {
        const full = typeof img.url_fullxfull === 'string' ? img.url_fullxfull : ''
        const medium = typeof img.url_570xN === 'string' ? img.url_570xN : ''
        const thumb = typeof img.url_170x135 === 'string' ? img.url_170x135 : ''
        const src = full || medium || thumb
        return src ? { src, thumb: thumb || medium || full } : null
      })
      .filter((x): x is { src: string; thumb: string } => !!x)
  }, [listing])
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)

  useEffect(() => {
    setSelectedMediaIndex(0)
  }, [listingId, media.length])

  const selectedMedia = media[selectedMediaIndex]?.src || listing?.imageUrl
  const hasAnyMedia = !!selectedMedia || media.length > 0
  const [mediaTimedOut, setMediaTimedOut] = useState(false)

  useEffect(() => {
    if (!listing) return
    if (hasAnyMedia) {
      setMediaTimedOut(false)
      return
    }
    setMediaTimedOut(false)
    const t = window.setTimeout(() => setMediaTimedOut(true), 1200)
    return () => window.clearTimeout(t)
  }, [listingId, hasAnyMedia, listing])
  const displayPrice =
    typeof listing?.price === 'number'
      ? `${String(listing.currency || 'USD')} ${listing.price.toFixed(2)}`
      : '—'

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Link
              to={listHref}
              className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-sky-700 hover:bg-sky-50 text-xl"
              aria-label="Back to Etsy products"
            >
              ←
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 line-clamp-2">Etsy listing</h1>
              {shopId ? <p className="text-xs text-gray-500 mt-0.5">Shop {shopId} · read-only</p> : null}
            </div>
          </div>
          {brandId ? (
            <Link
              to={`/brands/${brandId}/settings`}
              className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              aria-label="Settings"
            >
              ⚙️
            </Link>
          ) : null}
        </div>
      </header>

      <main className="p-4 space-y-4">
        {isLoading && <p className="text-sm text-gray-500">Loading product…</p>}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-900">
            {error instanceof Error ? error.message : 'Failed to load listing'}
          </div>
        )}
        {!isLoading && !error && listing && (
          <>
            <section className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="rounded-xl overflow-hidden bg-gray-100 border border-gray-200 aspect-[4/3] max-h-80 flex items-center justify-center">
                {hasAnyMedia ? (
                  <img
                    src={selectedMedia}
                    alt={String(listing.title || 'Etsy listing image')}
                    className="w-full h-full object-contain"
                  />
                ) : !mediaTimedOut ? (
                  <div role="status" aria-live="polite" className="flex flex-col items-center gap-2">
                    <div className="animate-spin rounded-full border-4 border-gray-200 border-t-sky-600 w-12 h-12" />
                    <span className="text-gray-500 text-sm">Loading media…</span>
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">No media</span>
                )}
              </div>
              {media.length > 1 ? (
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {media.map((img, idx) => (
                    <button
                      key={`${img.src}-${idx}`}
                      type="button"
                      onClick={() => setSelectedMediaIndex(idx)}
                      aria-label={`Media ${idx + 1}`}
                      className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                        idx === selectedMediaIndex ? 'border-sky-600 ring-2 ring-sky-200' : 'border-gray-200'
                      }`}
                    >
                      <img src={img.thumb} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              ) : null}
            </section>

            <section className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Summary</h2>
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium text-gray-500">Title</div>
                  <div className="text-base text-gray-900">{String(listing.title ?? '—')}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Etsy listing id</div>
                  <div className="text-base font-mono text-gray-900">{String(listing.id ?? listingId)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Status</div>
                  <div className="text-base text-gray-900">{String(listing.state ?? 'active')}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-500">Price</div>
                  <div className="text-base text-gray-900">{displayPrice}</div>
                </div>
              </div>
            </section>

            <section className="bg-white border border-gray-200 rounded-lg p-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Listing info</h2>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="text-gray-500">Price: </span>
                  <span className="text-gray-900">{displayPrice}</span>
                </p>
                <p>
                  <span className="text-gray-500">Quantity: </span>
                  <span className="text-gray-900">{String(listing.quantity ?? '—')}</span>
                </p>
                <p>
                  <span className="text-gray-500">Category: </span>
                  <span className="text-gray-900">
                    {Array.isArray(listing.categoryPath) && listing.categoryPath.length > 0
                      ? listing.categoryPath.join(' → ')
                      : '—'}
                  </span>
                </p>
              </div>
            </section>

            {Array.isArray(listing.tags) && listing.tags.length > 0 ? (
              <section className="bg-white border border-gray-200 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.tags.map((tag) => (
                    <span key={tag} className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                      {tag}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}

            {Array.isArray(listing.materials) && listing.materials.length > 0 ? (
              <section className="bg-white border border-gray-200 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Materials</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.materials.map((material) => (
                    <span key={material} className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                      {material}
                    </span>
                  ))}
                </div>
              </section>
            ) : null}

            {(listing as { description?: string }).description ? (
              <section className="bg-white border border-gray-200 rounded-lg p-4">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h2>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{String((listing as { description?: string }).description)}</p>
              </section>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}
